/**
 * FAQ 데이터 단일 소스
 *
 * 같은 Q&A를 schema(FAQPage)와 UI(FAQ 컴포넌트) 양쪽에서 사용하므로 DRY 통합.
 * - helper(crematorium/parlorRegion/facility)는 `toFaqPageMainEntity()`로 schema 형식 변환
 * - UI 컴포넌트(*RegionFAQ / *DetailFAQ)는 직접 매핑하여 표시
 *
 * Google FAQPage 가이드: schema의 답이 페이지에 실제로 노출되어야 RichResults 인정.
 */

export type FaqDataItem = { q: string; a: string };

export const CREMATORIUM_FAQ_DATA: FaqDataItem[] = [
  {
    q: "관내·관외 요금 차이는 무엇인가요?",
    a: "화장장이 위치한 시·군·구에 주민등록이 된 주민은 관내 요금이 적용됩니다. 타 지역 주민은 관외 요금이 적용되며, 통상 관외 요금이 수 배 높습니다. 당일 신분증(주민등록증) 지참이 필수입니다.",
  },
  {
    q: "화장장 예약은 어떻게 하나요?",
    a: "대부분의 화장장은 e-하늘 장사정보시스템(www.ehaneul.go.kr) 또는 해당 화장장 전화를 통해 예약합니다. 빈 일정이 없을 경우 인근 지역 화장장도 이용 가능합니다.",
  },
  {
    q: "감면 혜택은 어떻게 받나요?",
    a: "기초생활수급자(의료·생계급여), 국가유공자 등은 화장 요금을 전액 또는 일부 감면받을 수 있습니다. 화장장별 감면 대상이 다르므로 예약 시 해당 화장장에 확인하세요.",
  },
  {
    q: "고이장례연구소를 통해 화장장 예약을 도와주나요?",
    a: "고이장례연구소는 장례식장 선정·비용 비교·상조 연계를 지원합니다. 화장장 예약 절차 안내와 함께 장례 전반에 대한 무료 상담을 제공합니다.",
  },
];

export const PARLOR_REGION_FAQ_DATA: FaqDataItem[] = [
  {
    q: "장례식장 비용은 어떻게 산정되나요?",
    a: "장례식장 비용은 빈소 사용료(1일)·시설 사용료·식대·기타 부대비용으로 구성됩니다. 빈소 평수, 조문객 규모, 장례 일수(보통 2박 3일)에 따라 총비용이 결정되며, 동일 지역 내에서도 시설별로 가격 차이가 크므로 사전 비교가 필요합니다.",
  },
  {
    q: "공설과 사설 장례식장의 차이는 무엇인가요?",
    a: "공설 장례식장은 지자체·의료원 등이 운영하여 비용이 비교적 투명하고 안정적이며, 의료원 부설인 경우 임종~안치 동선이 유리합니다. 사설은 시설·서비스 수준이 다양하나 비용 차이가 크므로 견적을 받아 비교하는 것이 좋습니다.",
  },
  {
    q: "장례식장 예약은 언제 하나요?",
    a: "임종 직후 또는 임종 예정 시점에 장례식장을 결정하고 예약합니다. 사망진단서(또는 시체검안서)와 신분증을 지참하여 직접 또는 전화로 예약하며, 빈소 수급 상황에 따라 인접 지역 장례식장도 함께 고려하는 것이 안전합니다.",
  },
  {
    q: "고이장례연구소가 장례식장 선정을 도와주나요?",
    a: "고이장례연구소는 비용 비교, 빈소 사용 가능 여부, 시설 정보 등을 종합하여 유족의 상황에 맞는 장례식장을 무료로 추천합니다. 상조 연계와 장례 절차 전반의 안내도 함께 제공합니다.",
  },
];

export const FACILITY_FAQ_DATA: FaqDataItem[] = [
  {
    q: "공원묘지·납골당·자연장지의 차이는 무엇인가요?",
    a: "공원묘지는 매장 시설을, 납골당은 화장 후 유골을 봉안하는 시설을, 자연장지는 화장한 유골을 나무·잔디 등 자연 친화 공간에 모시는 시설을 의미합니다. 가족의 상황과 종교, 비용 등을 고려해 선택할 수 있습니다.",
  },
  {
    q: "장사시설 예약은 어떻게 하나요?",
    a: "각 시설은 운영 주체(지자체 또는 사설)에 따라 예약 방법이 다릅니다. 시설 공식 전화 또는 e-하늘 장사정보시스템(www.ehaneul.go.kr)을 통해 예약할 수 있으며, 사설 시설의 경우 별도 사전 상담이 필요할 수 있습니다.",
  },
  {
    q: "사용료 감면 혜택은 받을 수 있나요?",
    a: "기초생활수급자, 국가유공자, 관내 거주자 등은 사용료 감면 또는 우선 예약 혜택을 받을 수 있습니다. 시설별 조건이 다르므로 상황별 확인이 필요할 수 있습니다.",
  },
];

/**
 * FAQ 배열을 schema.org FAQPage.mainEntity 형식으로 변환
 */
export function toFaqPageMainEntity(items: FaqDataItem[]) {
  return items.map((item) => ({
    "@type": "Question" as const,
    name: item.q,
    acceptedAnswer: {
      "@type": "Answer" as const,
      text: item.a,
    },
  }));
}
